import React, { useRef } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useAccountAddressesAPI } from '../../apis/account-address-api';
import { useStorage } from '../../hooks/use-storage';
import { useSwal } from '../../hooks/use-swal';
import { useErrorHandler } from '../../hooks/use-error-handler';
import { SelectAddressModalTitle } from '../../components/location/select-address-modal-title';
import { layoutDefaults } from '../../utils/layout-defaults';
import { usePublicAPI } from '../../apis/public-api';
import { useAnalytics } from '../../utils/setup/analytics-setup';
import { SelectAddressModalBody } from '../../components/location/select-address-modal-body';

function useSelectAddressModal() {
  const storage = useStorage();
  const { config } = storage;
  const { swal } = useSwal({ storage });
  const analytics = useAnalytics();
  const { analyticsPageView } = analytics;
  const { handleErrorOnModal } = useErrorHandler();
  const { getAddressPrivacyPolicyFromRemote } = usePublicAPI();
  const { findAccountAddressesFromRemote } = useAccountAddressesAPI();
  const { t } = useTranslation();

  const addressesRef = useRef();

  const didOpen = () => {
    analyticsPageView('Select Address');
  };

  async function prepare() {
    addressesRef.current = null;
    let hasAddresses = false;

    try {
      const response = await findAccountAddressesFromRemote();
      const addresses = _.orderBy(
        response.data ?? [],
        [(address) => dayjs(address.updated_at)],
        ['desc']
      );

      addressesRef.current = addresses;
      hasAddresses = !_.isEmpty(addresses);
    } catch (error) {
      handleErrorOnModal(error);
    }

    return {
      hasAddresses,
    };

    // TODO: Implement prepare function
  }

  async function show({
    wasBackButtonClicked,
    avoidAnimation,
    prepareOnPreConfirm,
  }) {
    let address;
    let wasOtherAddressClicked = false;
    let wasDeleteAddressClicked = false;

    const result = await swal.fire({
      title: (
        <SelectAddressModalTitle
          style={{
            marginTop: layoutDefaults.biggerVerticalMargin,
          }}
          title={t('select-address-modal.title')}
          analytics={analytics}
          getPrivacyPolicyFromRemote={getAddressPrivacyPolicyFromRemote}
        />
      ),
      html: (
        <SelectAddressModalBody
          addresses={addressesRef.current}
          config={config}
          onSelect={async (pAddress) => {
            try {
              address = pAddress;
              await prepareOnPreConfirm?.call(this);

              swal.clickConfirm();
            } catch (error) {
              handleErrorOnModal(error);
            }
          }}
          onDelete={async (pAdress) => {
            address = pAdress;
            wasDeleteAddressClicked = true;

            swal.clickConfirm();
          }}
          onNewAddressClick={async () => {
            wasOtherAddressClicked = true;
            swal.clickConfirm();
          }}
        />
      ),
      showConfirmButton: false,
      showDenyButton: false,
      showCancelButton: true,
      cancelButtonText: t('cancel'),
      didOpen,
      showClass:
        wasBackButtonClicked || avoidAnimation
          ? null
          : {
              popup: 'animate__animated animate__slideInLeft animate__faster',
            },
    });

    if (result.isConfirmed) {
      result.address = address;
    }

    result.wasOtherAddressClicked = wasOtherAddressClicked;
    result.wasDeleteAddressClicked = wasDeleteAddressClicked;

    return result;
  }

  return {
    prepareSelectAddressModal: prepare,
    showSelectAddressModal: show,
  };
}

export { useSelectAddressModal };
